import React, { useMemo } from 'react';

import { Anchor, Group } from '@mantine/core';

import { routes } from '@/routes';

import { DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { ReportInfo } from '@/types/patientList';

import styles from './cellsV2.module.scss';

export const VendorsToCodeMapping: Record<string, string> = {
  abbott: 'ABT',
  boston_scientific: 'BSC',
  medtronic: 'MDT',
  biotronik: 'BIO',
  sorin: 'SOR'
};

/** Full vendor name for a list-row vendor slug (boston_scientific → Boston Scientific). */
export function vendorDisplayName(vendor?: string | null): string {
  if (!vendor) return '';
  return getVendorDisplayName(vendor as DeviceVendor);
}

const toTitleCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const alertColor = (color?: string) => {
  switch (color) {
    case 'RED':
      return 'var(--mantine-color-red-6)';
    case 'YELLOW':
      return 'var(--mantine-color-yellow-6)';
    case 'GREEN':
      return 'var(--mantine-color-teal-6)';
    default:
      return 'var(--mantine-color-gray-4)';
  }
};

interface ReportNameProps {
  report: ReportInfo;
}

export const ReportName: React.FC<ReportNameProps> = ({ report }) => {
  // POC shows the full vendor name (Boston Scientific, not BSC).
  const vendorLabel = useMemo(
    () => vendorDisplayName(report.device?.vendor) || 'N/A',
    [report.device?.vendor]
  );
  const reportTypeDisplay = useMemo(
    () => toTitleCase(report.reportType || ''),
    [report.reportType]
  );

  const label =
    report.color === 'GRAY'
      ? 'Unclassified'
      : report.color === 'RED'
        ? `${vendorLabel} Red Alert`
        : report.color === 'YELLOW'
          ? `${vendorLabel} Yellow Alert`
          : `${vendorLabel} ${reportTypeDisplay}`;

  return (
    <Group gap={6} wrap="nowrap">
      <span className={styles.dot} style={{ backgroundColor: alertColor(report.color) }} />
      <Anchor
        component="button"
        className={styles.reportLink}
        fz={14}
        onClick={() => {
          if (report.id) {
            window.open(
              routes.physician.transmissionReportDetail(report.id.toString()),
              '_blank',
              'noopener,noreferrer'
            );
          }
        }}>
        {label} {report.device?.type}
      </Anchor>
    </Group>
  );
};
